"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionMapPoint = void 0;
const core_api_1 = require("@ikegps/core-api");
const core_shared_1 = require("@ikegps/core-shared");
const theme_1 = require("../../components/theme");
const theme_palette_1 = require("../../components/theme.palette");
const dom_1 = require("../../dom");
const index_1 = require("../../i18n/index");
const mithril_1 = require("../../mithril");
const services_1 = require("../../services");
const page_state_1 = require("../../state/page.state");
const store_1 = require("../../store");
const projection_cache_1 = require("../google/projection.cache");
const point_collection_cache_1 = require("./point.collection.cache");
const CSS = {
    container: 'c-MapCollection',
    title: 'c-MapCollection__title',
    titleText: 'c-MapCollection__titleText',
    point: 'c-MapCollection__point',
    line: 'c-MapCollection__line',
    isHovered: 'c-MapCollection--is-hovered',
    isSelected: 'c-MapCollection--is-selected',
    isInspected: 'c-MapCollection--is-inspected'
};
const CIRCLE_RADIUS = theme_1.DefaultTheme.spacingFactor * 1.5;
const CIRCLE_SIZE_PX = CIRCLE_RADIUS * 2 + 'px';
const CIRCLE_BORDER_SIZE_THICK = 3;
const CIRCLE_BORDER_SIZE = 2;
const TITLE_WIDTH = 120;
const TITLE_PADDING = theme_1.DefaultTheme.spacingFactor;
const ZOOM_LINE_LEVEL = 18;
const SITE_TYPE_COLORS = {
    1: 'rgb(82,11,144)', // purple
    2: 'rgb(72,187,120)' // green
};
const DROP_POLE_COLORS = {
    1: 'rgb(255,0,0)', // red
    2: 'rgb(0,255,0)' // green
};
(0, theme_1.addStyles)({
    [CSS.container]: {
        position: 'absolute'
    },
    [CSS.point]: {
        height: CIRCLE_SIZE_PX,
        width: CIRCLE_SIZE_PX,
        minWidth: CIRCLE_SIZE_PX,
        maxWidth: CIRCLE_SIZE_PX,
        borderRadius: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'absolute',
        border: `${CIRCLE_BORDER_SIZE}px solid ${(0, theme_1.rgba)(theme_palette_1.BLACK, 0.5)}`
    },
    [`${CSS.isHovered}, .${CSS.isSelected}, .${CSS.isInspected}`]: {
        zIndex: 99
    },
    [`${CSS.point}.${CSS.isSelected}`]: {
        boxShadow: `0 0 0 3px rgba(${theme_palette_1.ColorPaletteOrange.C700}, 0.24), 0 0 0 3px rgba(${theme_palette_1.ColorPaletteOrange.C700}, 0.64)`,
        border: `${CIRCLE_BORDER_SIZE}px solid rgba(255, 255, 255, 0.87)`
    },
    [`${CSS.point}.${CSS.isInspected}`]: {
        boxShadow: `0 0 0 3px rgba(${theme_palette_1.ColorPaletteBlue.C700}, 0.24), 0 0 0 3px rgba(${theme_palette_1.ColorPaletteBlue.C700}, 0.64)`,
        border: `${CIRCLE_BORDER_SIZE}px solid rgba(255, 255, 255, 0.87)`
    },
    [`${CSS.point}.${CSS.isHovered}`]: {
        cursor: 'pointer',
        boxShadow: theme_1.DefaultTheme.shadow.box.dp2w5
    },
    [`${CSS.point}.${CSS.isHovered}.${CSS.isSelected}`]: {
        boxShadow: `0 0 0 5px rgba(${theme_palette_1.ColorPaletteOrange.C700}, 0.24), 0 0 0 5px rgba(${theme_palette_1.ColorPaletteOrange.C700}, 0.64)`
    },
    [CSS.title]: {
        fontFamily: theme_1.DefaultTheme.font.familyCondensed,
        fontWeight: theme_1.DefaultTheme.font.weight.bold,
        height: theme_1.DefaultTheme.space(6),
        lineHeight: theme_1.DefaultTheme.space(6),
        fontSize: '125%',
        position: 'absolute',
        width: `${TITLE_WIDTH}px`,
        textAlign: 'center',
        'pointer-events': 'none'
    },
    [CSS.titleText]: {
        backgroundColor: '#FFF',
        boxShadow: theme_1.DefaultTheme.shadow.box.dp2,
        padding: `0 ${TITLE_PADDING}px`,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
    }
});
const GREY_COLOR = 'rgb(160,174,192)';
exports.CollectionMapPoint = {
    view(state, summary, point) {
        var _a;
        const hoverId = page_state_1.CurrentState.hoverId;
        const isHovered = hoverId === summary.revision;
        const isSelected = page_state_1.CurrentState.Tag.isCollectionSelected(summary.tags[0], summary.uuid);
        const isInspected = page_state_1.CurrentState.inspectedCollectionRevision === summary.revision;
        const isTagHovered = summary.tags.indexOf(hoverId) > -1;
        const isDetailShown = isHovered || state.showDetail;
        const stateCSS = `${isInspected ? CSS.isInspected : ''} ${isSelected ? CSS.isSelected : ''} ${(isTagHovered || isHovered) ? CSS.isHovered : ''}`;
        let collection = (0, mithril_1.m)('div', {
            key: summary.objectId,
            [dom_1.DRAGON_ID]: summary.revision,
            [dom_1.DRAGON_TYPE]: dom_1.DragonDomTypes.COLLECTION,
            className: `${CSS.point} ${stateCSS}`,
            style: {
                // backgroundColor: ColorHash.hash(summary.tags[0]),
                top: (point.y - CIRCLE_RADIUS - CIRCLE_BORDER_SIZE) + 'px',
                left: (point.x - CIRCLE_RADIUS - CIRCLE_BORDER_SIZE) + 'px'
            }
        });
        // special collection coloring feature for Crown Castle
        // https://surveylab.atlassian.net/browse/IKE4-2997
        if (services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_COLLECTION_COLORING)) {
            if (summary.siteType) {
                const siteTypeInt = parseInt(summary.siteType, 10);
                collection.attrs.style.backgroundColor = isNaN(siteTypeInt) ? GREY_COLOR : SITE_TYPE_COLORS[siteTypeInt];
            }
            else if (!(summary.dropPole === null || summary.dropPole === undefined)) {
                collection.attrs.style.backgroundColor = summary.dropPole ? DROP_POLE_COLORS[1] : DROP_POLE_COLORS[2];
            }
            else {
                collection.attrs.style.backgroundColor = GREY_COLOR;
            }
            const borderColor = core_shared_1.ColorHash.hash(summary.tags[0]);
            if (!isInspected) {
                collection.attrs.style.border = `${CIRCLE_BORDER_SIZE}px solid ${borderColor}`;
            }
        }
        else if (services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_COLOR_FLAG)) {
            if (summary.mapMarkerColor) {
                collection.attrs.style.backgroundColor = (_a = summary.mapMarkerColor) !== null && _a !== void 0 ? _a : GREY_COLOR;
                const borderColor = core_shared_1.ColorHash.hash(summary.tags[0]);
                if (!isInspected) {
                    collection.attrs.style.border = `${CIRCLE_BORDER_SIZE_THICK}px solid ${borderColor}`;
                }
            }
            else {
                //if mapMarkerColor is not set, use the job color
                collection.attrs.style.backgroundColor = core_shared_1.ColorHash.hash(summary.tags[0]);
            }
        }
        else {
            collection.attrs.style.backgroundColor = core_shared_1.ColorHash.hash(summary.tags[0]);
        }
        if (isDetailShown) {
            let title = summary.title;
            if (title == null) {
                title = (0, index_1.Translate)(index_1.T.COLLECTION_MISSING_TITLE);
            }
            delete collection.attrs.key;
            collection.attrs.onclick = state.inspectHovered;
            collection = (0, mithril_1.m)('div', { key: summary.objectId }, [
                collection,
                (0, mithril_1.m)('div', {
                    key: 'hovered-' + summary.objectId,
                    [dom_1.DRAGON_ID]: summary.revision,
                    [dom_1.DRAGON_TYPE]: dom_1.DragonDomTypes.COLLECTION,
                    className: `${CSS.title} ${stateCSS}`,
                    style: {
                        top: point.y - (theme_1.DefaultTheme.spacingFactor * 10) + 'px',
                        left: point.x - (TITLE_WIDTH / 2) + (TITLE_PADDING / 2) + 'px'
                    }
                }, (0, mithril_1.m)('span', { className: CSS.titleText }, title))
            ]);
        }
        if (isSelected || isInspected || isDetailShown || state.zoom > ZOOM_LINE_LEVEL) {
            if (services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_MAP_VECTOR)
                || services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_GROUP_IKE_ANALYZE)) {
                return { collection, line: exports.CollectionMapPoint.viewLines(state, summary, point) };
            }
        }
        return { collection, line: null };
    },
    viewLines(state, summary, point) {
        const collection = store_1.AppStore.Collection.get(summary.revision);
        if (collection == null) {
            return null;
        }
        const location = point_collection_cache_1.PointCollectionCache.getCollectionLocation(summary.revision);
        if (location == null) {
            return null;
        }
        const lineInfos = point_collection_cache_1.PointCollectionCache.getCollectionVectors(summary.revision);
        const lines = [];
        const useColorCoding = services_1.Services.Token.has(core_api_1.IkeToken.PHOTOMEASURE_COLOR_CODING);
        for (const lineInfo of lineInfos) {
            const { field, target } = lineInfo;
            const targetPoint = projection_cache_1.PointCache.getPoint(target.latitude, target.longitude);
            const spanColor = core_shared_1.FormModelFieldSubForm.getSpanColor(field.parent);
            let color = useColorCoding && spanColor ? spanColor : core_shared_1.ColorHash.hash(field.schema.id);
            if (core_shared_1.CollectionModelFieldAnchor.isAnchorField(field)) {
                color = `rgb(${theme_palette_1.ANCHOR_PINK})`;
            }
            const line = createLine(point.x, point.y, targetPoint.x, targetPoint.y, color);
            line.attrs[dom_1.DRAGON_ID] = field.id;
            line.attrs[dom_1.DRAGON_TYPE] = 'field';
            lines.push(line);
        }
        return lines;
    }
};
const LINE_WIDTH = 1;
const LINE_HIT_BOX = LINE_WIDTH * 8;
const LineCss = {
    container: 'c-MapLine',
    hitBox: 'c-MapLine__hitbox',
    lineFill: 'c-MapLine__fill',
    info: 'c-MapLine__info'
};
function createLine(x1, y1, x2, y2, fillColor, text) {
    const topLeft = { x: Math.min(x1, x2), y: Math.min(y1, y2) };
    const bottomRight = { x: Math.max(x1, x2), y: Math.max(y1, y2) };
    const width = Math.abs(topLeft.x - bottomRight.x);
    const height = Math.abs(topLeft.y - bottomRight.y);
    const length = Math.sqrt(width * width + height * height);
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const topHitBox = (y1 - LINE_HIT_BOX / 2) + 'px';
    const left = x1 + 'px';
    const lineHitBox = (0, mithril_1.m)('div', {
        className: LineCss.hitBox,
        style: {
            width: `${length}px`,
            position: 'absolute',
            display: 'flex',
            top: topHitBox,
            left,
            height: LINE_HIT_BOX + 'px',
            transform: `rotate(${angle}rad)`,
            'transform-origin': `0px ${LINE_HIT_BOX / 2}px`
        }
    });
    const line = (0, mithril_1.m)('div', {
        className: LineCss.lineFill,
        style: {
            width: `${length}px`,
            position: 'absolute',
            top: y1 + 'px',
            left,
            outline: '1px solid ' + fillColor,
            height: 0,
            transform: `rotate(${angle}rad)`,
            'transform-origin': '0px 0px'
        }
    });
    const children = [lineHitBox, line];
    return (0, mithril_1.m)('div', { className: LineCss.container }, children);
}
//# sourceMappingURL=point.collection.js.map